import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../config/Supabase";
import "../../styles/Article.css";
import "../../styles/HerbCategories.css";
import SEO from "../utils/SEO";
import SubscriptionComponent from "../utils/SubscriptionComponent";
import { trackCTAClick } from "../../utils/analytics";
import HERB_CATEGORIES from "../../data/herbCategories";
import { herbSlug, isHerbIndexable } from "../../utils/herbSlug";
import { REVIEWER, EDITORIAL_PATH, reviewerSchema } from "../../config/eeat";

const BASE_URL = "https://joinsarv.com";

// First N sentences of a text field — for concise, extractable FAQ answers (GEO).
const firstSentences = (t, n = 2) =>
  String(t || "")
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?])\s+/)
    .slice(0, n)
    .join(" ")
    .trim();

const prettyDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? ""
    : dt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

// Shared, public-facing herb monograph — matches the article design system.
// Used by the public /herbs/:slug page (indexable) and the admin view (noindex).
// Categorical fields render as pill "bubbles"; a trial CTA + newsletter + a
// same-category carousel turn it into a conversion + internal-linking surface.

const toChips = (val) => {
  if (Array.isArray(val)) return val.map((v) => String(v).trim()).filter(Boolean);
  if (!val) return [];
  return String(val)
    .split(/[,;/]|\band\b/i)
    .map((s) => s.trim())
    .filter(Boolean);
};

const paragraphs = (text) =>
  String(text || "")
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

const truncate = (t, n) => (t && t.length > n ? `${t.slice(0, n)}…` : t || "");

const ChipGroup = ({ label, items, variant = "default" }) => {
  const chips = (items || []).filter(Boolean);
  if (!chips.length) return null;
  return (
    <div className="herb-facts__group">
      <span className="herb-facts__label">{label}</span>
      <div className="herb-chips">
        {chips.map((c, i) => (
          <span key={i} className={`herb-chip herb-chip--${variant}`}>
            {c}
          </span>
        ))}
      </div>
    </div>
  );
};

const HerbCarousel = ({ items }) => {
  const ref = useRef(null);
  const scroll = (dir) =>
    ref.current && ref.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  return (
    <div className="herb-carousel">
      <button
        type="button"
        className="herb-carousel__btn herb-carousel__btn--prev"
        aria-label="Scroll left"
        onClick={() => scroll(-1)}
      >
        ‹
      </button>
      <div className="herb-carousel__track" ref={ref}>
        {items.map((h) => (
          <Link key={h.id} to={`/herbs/${herbSlug(h)}`} className="herb-carousel__card">
            <div className="herb-carousel__imgwrap">
              <img src={h.img_url} alt={h.title} loading="lazy" />
            </div>
            <div className="herb-carousel__cardbody">
              <div className="herb-carousel__title">{h.title}</div>
              <div className="herb-carousel__desc">{truncate(h.summary, 72)}</div>
            </div>
          </Link>
        ))}
      </div>
      <button
        type="button"
        className="herb-carousel__btn herb-carousel__btn--next"
        aria-label="Scroll right"
        onClick={() => scroll(1)}
      >
        ›
      </button>
    </div>
  );
};

const HerbMonograph = ({ herb, indexable = false }) => {
  const [related, setRelated] = useState([]);
  const [relatedCat, setRelatedCat] = useState(null);

  // Same-category herbs for the "Learn more" carousel — matched by therapeutic
  // actions overlapping a category's subcategories (mirrors the mobile app).
  useEffect(() => {
    if (!herb) return;
    const actions = (Array.isArray(herb.therapeutic_actions) ? herb.therapeutic_actions : []).map(
      (a) => String(a).toLowerCase()
    );
    const cat = HERB_CATEGORIES.find((c) =>
      c.subcategories.some((s) => actions.includes(s.toLowerCase()))
    );
    if (!cat) return;
    let active = true;
    (async () => {
      const subs = cat.subcategories.map((s) => s.toLowerCase());
      const { data } = await supabase
        .from("herbs")
        .select("id,title,summary,img_url")
        .overlaps("therapeutic_actions", subs)
        .neq("id", herb.id)
        .order("popularity_score", { ascending: false, nullsFirst: false })
        .limit(15);
      if (active) {
        setRelatedCat(cat.name);
        setRelated(data || []);
      }
    })();
    return () => {
      active = false;
    };
  }, [herb]);

  if (!herb) return null;

  const slug = herbSlug(herb);
  const url = `${BASE_URL}/herbs/${slug}`;
  const canIndex = indexable && isHerbIndexable(herb);
  const description = herb.summary
    ? String(herb.summary).slice(0, 300)
    : `${herb.title}: traditional uses, benefits, dosage, and safety in herbal medicine.`;

  const dosageEntries =
    herb.dosage && typeof herb.dosage === "object" ? Object.entries(herb.dosage) : [];
  const safety = [
    ["Contraindications", herb.contraindications],
    ["Side effects", herb.side_effects],
    ["Interactions", herb.interactions],
  ].filter(([, v]) => v && String(v).trim());
  const reviewedOn = prettyDate(herb.updated_at);

  // Data-driven FAQ (from existing fields) — drives People-Also-Ask + AI Overviews
  // via FAQPage schema, with no new content to author. Only include answerable Qs.
  const faq = [];
  if (herb.medicinal_uses || herb.summary) {
    faq.push({
      q: `What is ${herb.title} used for?`,
      a: firstSentences(herb.medicinal_uses || herb.summary, 3),
    });
  }
  if (herb.benefits) {
    faq.push({ q: `What are the benefits of ${herb.title}?`, a: firstSentences(herb.benefits, 3) });
  }
  if (dosageEntries.length) {
    faq.push({
      q: `How do you take ${herb.title}?`,
      a: `Common preparations include ${dosageEntries
        .map(([m]) => String(m).toLowerCase())
        .join(", ")}. See the dosage guidance above, and consult a qualified practitioner for amounts suited to you.`,
    });
  }
  if (safety.length) {
    faq.push({
      q: `Is ${herb.title} safe?`,
      a: `${firstSentences(
        herb.contraindications || herb.side_effects || herb.interactions,
        2
      )} As with any herb, consult a qualified healthcare provider before use — especially if you are pregnant, nursing, or taking medication.`,
    });
  }

  // Structured data (only when public + substantive) — MedicalWebPage + Breadcrumb
  // for rich results and AI-answer citation.
  const medSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: herb.title,
    description,
    ...(herb.img_url ? { image: herb.img_url } : {}),
    url,
    inLanguage: "en",
    about: {
      "@type": "Substance",
      name: herb.title,
      ...(herb.scientific_name ? { alternateName: herb.scientific_name } : {}),
    },
    ...(herb.updated_at ? { lastReviewed: herb.updated_at } : {}),
    author: { "@type": "Organization", name: "Sarv Health Team", url: BASE_URL },
    reviewedBy: reviewerSchema(),
    publisher: {
      "@type": "Organization",
      name: "Sarv",
      url: BASE_URL,
      logo: { "@type": "ImageObject", url: `${BASE_URL}/v.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
      { "@type": "ListItem", position: 2, name: "Herbs", item: `${BASE_URL}/herbCategories` },
      { "@type": "ListItem", position: 3, name: herb.title, item: url },
    ],
  };
  const faqSchema = faq.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <>
      <article className="article-page herb-page">
        <SEO
          title={`${herb.title} — Benefits, Uses, Dosage & Safety | Sarv`}
          description={description}
          canonicalPath={`/herbs/${slug}`}
          keywords={`${herb.title}${herb.scientific_name ? `, ${herb.scientific_name}` : ""}, ${herb.title} benefits, ${herb.title} uses, ${herb.title} dosage, ${herb.title} side effects, herbal medicine`}
          image={herb.img_url}
          noIndex={!canIndex}
        />
        {canIndex && (
          <>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(medSchema) }}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
            />
            {faqSchema && (
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
              />
            )}
          </>
        )}

        <nav className="article-page__breadcrumb" aria-label="breadcrumb">
          <Link to="/">Home</Link> › <Link to="/herbCategories">Herbs</Link> ›{" "}
          <span>{herb.title}</span>
        </nav>

        <h1 className="article-page__title herb-page__title">{herb.title}</h1>
        {(herb.scientific_name || herb.family) && (
          <p className="herb-page__sci">
            {herb.scientific_name && <em>{herb.scientific_name}</em>}
            {herb.scientific_name && herb.family ? " · " : ""}
            {herb.family}
          </p>
        )}

        <p className="article-page__review">
          Reviewed by {REVIEWER.name}
          {reviewedOn ? ` · Updated ${reviewedOn}` : ""} ·{" "}
          <Link to={EDITORIAL_PATH}>Our editorial process</Link>
        </p>

        {herb.img_url && (
          <figure className="article-page__hero-wrap">
            <img className="article-page__hero" src={herb.img_url} alt={herb.title} loading="eager" />
          </figure>
        )}

        {herb.summary && <p className="article-page__lead">{herb.summary}</p>}

        <div className="herb-facts">
          <ChipGroup label="Plant nature" items={toChips(herb.nature)} variant="nature" />
          <ChipGroup label="Parts used" items={toChips(herb.parts_used)} />
          <ChipGroup label="Therapeutic actions" items={toChips(herb.therapeutic_actions)} variant="action" />
          <ChipGroup
            label="Organs & systems affected"
            items={toChips(herb.organs_systems_affected)}
            variant="organ"
          />
          <ChipGroup label="Also known as" items={toChips(herb.common_names)} variant="muted" />
        </div>

        {herb.medicinal_uses && (
          <section>
            <h2 className="article-page__h2">Medicinal Uses</h2>
            {paragraphs(herb.medicinal_uses).map((p, i) => (
              <p key={i} className="article-page__p">
                {p}
              </p>
            ))}
          </section>
        )}

        <aside className="article-page__cta article-page__cta--mid">
          <p>
            Want a natural routine matched to your body and goals?{" "}
            <Link
              to="/start?source=herb"
              onClick={() => trackCTAClick("herb_mid_start_trial", "herb-mid")}
            >
              Get your personalized plan →
            </Link>
          </p>
        </aside>

        {herb.benefits && (
          <section>
            <h2 className="article-page__h2">Benefits</h2>
            {paragraphs(herb.benefits).map((p, i) => (
              <p key={i} className="article-page__p">
                {p}
              </p>
            ))}
          </section>
        )}

        {dosageEntries.length > 0 && (
          <section>
            <h2 className="article-page__h2">How to Use &amp; Dosage</h2>
            <div className="herb-dosage">
              {dosageEntries.map(([method, value], i) => (
                <div key={i} className="herb-dosage__card">
                  <span className="herb-chip herb-chip--action">{method}</span>
                  {Array.isArray(value) ? (
                    <ul className="herb-dosage__list">
                      {value.map((step, s) => (
                        <li key={s}>{step}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="article-page__p herb-dosage__text">{value}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {safety.length > 0 && (
          <section className="herb-safety">
            <h2 className="article-page__h2">Safety &amp; Precautions</h2>
            {safety.map(([label, value]) => (
              <div key={label} className="herb-safety__item">
                <span className="herb-safety__label">{label}</span>
                <p className="article-page__p">{value}</p>
              </div>
            ))}
          </section>
        )}

        {(herb.plant_constituents || herb.flower_essence || herb.other_names || herb.attribute) && (
          <section>
            <h2 className="article-page__h2">More Info</h2>
            <dl className="herb-meta">
              {herb.other_names && (
                <>
                  <dt>Other names</dt>
                  <dd>{herb.other_names}</dd>
                </>
              )}
              {herb.plant_constituents && (
                <>
                  <dt>Key constituents</dt>
                  <dd>{herb.plant_constituents}</dd>
                </>
              )}
              {herb.attribute && (
                <>
                  <dt>Attribute</dt>
                  <dd>{herb.attribute}</dd>
                </>
              )}
              {herb.flower_essence && (
                <>
                  <dt>Flower essence</dt>
                  <dd>{herb.flower_essence}</dd>
                </>
              )}
            </dl>
          </section>
        )}

        {faq.length > 0 && (
          <section className="article-page__faq">
            <h2 className="article-page__h2">Frequently asked questions</h2>
            {faq.map((f, i) => (
              <div key={i} className="article-page__faq-item">
                <h3 className="article-page__faq-q">{f.q}</h3>
                <p className="article-page__p">{f.a}</p>
              </div>
            ))}
          </section>
        )}

        <p className="article-page__disclaimer">
          This information is for general wellness and educational purposes only and is not
          medical advice. Consult a qualified healthcare provider before using any herb —
          especially if you are pregnant, nursing, or taking medication.
        </p>

        <aside className="article-page__cta">
          <p>
            Want to know which herbs suit your body type?{" "}
            <Link to="/quiz" onClick={() => trackCTAClick("herb_dosha_quiz", "herb-end")}>
              Take the free Ayurvedic dosha quiz →
            </Link>
          </p>
        </aside>

        {related.length > 0 && (
          <section className="herb-learnmore">
            <h2 className="article-page__h2">
              Learn more{relatedCat ? ` — more ${relatedCat} herbs` : ""}
            </h2>
            <HerbCarousel items={related} />
          </section>
        )}
      </article>

      <SubscriptionComponent />
    </>
  );
};

export default HerbMonograph;
